import { useState } from 'react';
import { Box, Button, Typography, Grid, Card, CardContent, Chip, Stack } from '@mui/material';
import { Add as AddIcon, People as PeopleIcon, EmojiEvents as TrophyIcon, Category as CategoryIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchTournaments } from '../../services/api';
import { format } from 'date-fns';
import CreateTournamentDialog from '../../components/tournaments/CreateTournamentDialog';
import { GENDER_LABELS, TOURNAMENT_TYPE_LABELS, TournamentType } from '../../constants/userConstants';
import { useAuth } from '../../contexts/AuthContext';
import { canCreateTournament } from '../../utils/tournamentPermissions';

const TournamentsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const { data: tournaments, isLoading, error } = useQuery(['tournaments'], fetchTournaments);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ongoing':
        return 'success';
      case 'upcoming':
        return 'info';
      case 'completed':
        return 'default';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading tournaments</div>;

  const canCreate = canCreateTournament(user);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Tournaments</Typography>
        {canCreate && (
          <Button 
            variant="contained" 
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Tournament
          </Button>
        )}
      </Box>

      <CreateTournamentDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
      />

      <Grid container spacing={3}>
        {tournaments?.map((tournament) => (
          <Grid item xs={12} sm={6} md={4} key={tournament.id}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                }
              }}
              onClick={() => navigate(`/tournaments/${tournament.id}`)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="div">
                    {tournament.name}
                  </Typography>
                  <Chip
                    label={tournament.status}
                    color={getStatusColor(tournament.status)}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {tournament.location}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {format(new Date(tournament.start_date), 'MMM dd, yyyy')} - {format(new Date(tournament.end_date), 'MMM dd, yyyy')}
                </Typography>
                {tournament.description && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {tournament.description}
                  </Typography>
                )}
                {(tournament.type || tournament.allowed_genders || tournament.min_level || tournament.max_level) && (
                  <Box sx={{ mt: 2 }}>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {tournament.type && (
                        <Chip
                          icon={<CategoryIcon />}
                          label={TOURNAMENT_TYPE_LABELS[tournament.type as TournamentType]}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      )}
                      {tournament.allowed_genders && tournament.allowed_genders.length > 0 && (
                        tournament.allowed_genders.map((gender) => (
                          <Chip
                            key={gender}
                            icon={<PeopleIcon />}
                            label={GENDER_LABELS[gender]}
                            size="small"
                            variant="outlined"
                          />
                        ))
                      )}
                      {(tournament.min_level || tournament.max_level) && (
                        <Chip
                          icon={<TrophyIcon />}
                          label={`${tournament.min_level || 'Any'} - ${tournament.max_level || 'Any'}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {tournaments?.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No tournaments yet
          </Typography>
          {canCreate ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Create your first tournament to get started
              </Typography>
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={() => setCreateDialogOpen(true)}
              >
                Create Tournament
              </Button>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Check back soon for upcoming tournaments
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default TournamentsPage;
